import type { SSOPrefillData } from '../types';

/** Chip / label helpers shared by TurboChat (drawer) and TurboChatPage (full page). */

export function isSsoSubmitChip(optionId?: string, label?: string): boolean {
  return (
    optionId === 'submit_sso' ||
    optionId === 'open_sso_form' ||
    label === 'Submit SSO configuration' ||
    label === 'Open SSO form'
  );
}

export function isContinueChatChip(optionId?: string, label?: string): boolean {
  return (
    optionId === 'continue_chat' ||
    label === 'Continue in chat' ||
    label === 'Add more details in chat' ||
    label === 'Paste Entity ID / ACS' ||
    label === 'Paste redirect URL' ||
    label === 'Not now'
  );
}

/** True when the chip means "stop here / SSO later" after Setup. */
export function isPostSetupNotNowChip(optionId?: string, label?: string): boolean {
  return (
    optionId === 'continue_chat' &&
    /^(not now|later|maybe later|skip|no thanks|that'?s (?:it|all)|i'?m done)$/i.test(
      String(label || '').trim(),
    )
  );
}

export function continueChatFollowUpMessage(label: string): string {
  if (label === 'Paste Entity ID / ACS') {
    return (
      'I will paste the app Entity ID and ACS URL (or SAML metadata) for SSO. ' +
      'Please collect those next — do not submit SSO until they are filled.'
    );
  }
  if (label === 'Paste redirect URL') {
    return (
      'I will paste the OIDC redirect / callback URL(s). ' +
      'Please collect those next — do not submit SSO until they are filled.'
    );
  }
  if (isPostSetupNotNowChip('continue_chat', label)) {
    return (
      "I've finished setting up the application for now — I don't want to configure SSO yet."
    );
  }
  return 'I want to continue in chat and adjust or ask about the SSO setup we were working on.';
}

/**
 * Prefer agent/session Submit (IdP soft-confirm + SP gates).
 * Keep direct prefill POST only as a last-resort fallback when the agent path is unavailable.
 */
export type SsoSubmitChipPlan =
  | { mode: 'agent'; optionId: string; ssoPrefill?: SSOPrefillData }
  | { mode: 'direct_prefill'; prefill: SSOPrefillData }
  | { mode: 'skip' };

export function planSsoSubmitChip(args: {
  optionId?: string;
  label: string;
  pendingSsoPrefill?: SSOPrefillData | null;
  hasConversation: boolean;
}): SsoSubmitChipPlan {
  if (!isSsoSubmitChip(args.optionId, args.label)) return { mode: 'skip' };
  const optionId =
    args.optionId === 'open_sso_form' || args.optionId === 'submit_sso'
      ? args.optionId
      : 'submit_sso';
  const prefill = args.pendingSsoPrefill || undefined;

  // Agent path is preferred whenever a conversation exists — pass prefill so SP endpoints survive.
  if (args.hasConversation || optionId === 'open_sso_form') {
    return { mode: 'agent', optionId, ssoPrefill: prefill };
  }

  // No conversation yet: only direct-submit if we already have a bound prefill.
  if (prefill) {
    return { mode: 'direct_prefill', prefill };
  }

  return { mode: 'agent', optionId, ssoPrefill: prefill };
}
